/**
 * 방 접속 자격 보관 — §10 키·계정 세션.
 *
 * host_key/view_key 는 서버가 발급한 128bit 키다. 계정 세션 쿠키(HttpOnly)는
 * 브라우저가 관리하고, 여기서는 방 스코프 키만 다룬다.
 *
 * 저장소는 sessionStorage — 탭을 닫으면 사라진다(방 종료와 함께 폐기되는
 * 익명 토큰의 수명과도 맞다, §10.0).
 */

export interface RoomCredential {
  /** 불투명 방 ID — URL 경로에 실리는 축(§10.0) */
  room: string
  /**
   * 구 host_key/view_key — **TRANS_LEGACY_ROOM_KEYS=1 서버에서만** 오는
   * 전환기 자격. 기본(0)이면 빈 문자열이고 자격은 `token` 뿐이다.
   */
  key: string
  role: 'host' | 'viewer'
  /**
   * 서명 토큰(§5.2 정본 자격) — 개설/입장 응답이 준다. TTL 10분이라
   * lib/token.ts 의 RoomAuth 가 만료 전 재교환하며 여기 값을 갱신한다.
   * 구 키가 이미 같은 저장소에 있으므로 추가 노출은 없다(토큰이 더 약하다).
   */
  token?: string
  /** 토큰 만료(epoch ms) — 발급 응답의 token_ttl_s 로 계산 */
  tokenExp?: number
  /**
   * 6자리 입장 패스코드 — 호스트(구두 공유용)와 **패스코드로 입장한
   * 뷰어**(토큰 재교환용 — §5.2 "재교환이 곧 갱신") 가 보관한다.
   * 방 ID 와 별개 축이며, 어떤 URL·링크에도 싣지 않는다(§10.0).
   */
  passcode?: string
  /**
   * view_key — 호스트가 **공개 방 직통 링크**(`/v/{room}#k=…`)를 만들 때만
   * 쓴다(§10.2). 비공개 방에서는 링크를 만들지 않으므로 쓰이지 않는다.
   */
  viewKey?: string
  /**
   * 직통 링크로 들어온 뷰어의 **재교환 비밀**(`#k=` 값). 패스코드가 없는
   * 이 경로는 `/api/rooms/{room}/link` 로 토큰을 받고 갱신도 같은 비밀로
   * 같은 경로를 다시 지난다(lib/token.ts). URL 에는 다시 싣지 않는다.
   */
  linkKey?: string
  src: string
  dst: string
  /**
   * 양방향 통역 여부(§ bidi). 켜면 src/dst 는 방향이 아니라 언어 쌍이고
   * 어느 쪽으로 말해도 반대쪽으로 통역한다. 구 자격에는 없으므로(undefined)
   * 단방향으로 취급한다.
   */
  bidirectional?: boolean
  /** opt-in 공개 입장 방 여부(§10.0) */
  publicEntry?: boolean
  /** S28 회의 이름. 구 자격에는 없다 */
  name?: string
  /** S28 회의 주제. 구 자격에는 없다 */
  topic?: string
}

/**
 * 역할별 저장 키(TASK_sec_fix_f #2, codex P2). 한 키에 마지막 자격만 두면
 * 호스트 탭에서 같은 방의 뷰어 링크를 여는 순간 호스트 자격이 뷰어 자격으로
 * 덮여 `/host` 복귀가 랜딩으로 튕겼다. 호스트·뷰어를 따로 두면 링크 교환은
 * 뷰어 칸만 쓰고 호스트 칸은 그대로다.
 */
const KEYS = {
  host: 'omnitrans:room:host',
  viewer: 'omnitrans:room:viewer',
} as const

/** 역할별 칸 이전의 단일 저장 키 — 한 번 이행하고 지운다. */
const LEGACY_KEY = 'omnitrans:room'

/**
 * 옛 키의 자격을 역할 칸으로 **한 번** 이행한다(TASK_sec_fix_g #3, codex
 * 3차 P2). 번들이 바뀐 뒤 옛 키만 남은 탭은 호스트 새로고침·재입장과 뷰어
 * 링크 재방문이 전부 "자격 없음" 이 됐다. 새 칸에 이미 값이 있으면 그쪽이
 * 우선이다.
 *
 * 옛 키는 **새 칸 쓰기가 성공한 뒤에만** 지운다(TASK_sec_fix_h #2, codex
 * 4차 P2) — 먼저 지우고 쓰면 저장 공간이 가득 찬 탭에서 유일한 자격이
 * 사라졌다. 쓰기가 실패하면 옛 항목이 남고 `loadSlot` 이 거기서 읽는다.
 * 파싱 불가·역할 불명은 자격이 아니므로 그냥 지운다.
 */
function migrateLegacy(): void {
  try {
    const raw = sessionStorage.getItem(LEGACY_KEY)
    if (raw === null) return
    const cred = parseLegacy(raw)
    if (cred === null) {
      sessionStorage.removeItem(LEGACY_KEY)
      return
    }
    if (sessionStorage.getItem(KEYS[cred.role]) === null) {
      sessionStorage.setItem(KEYS[cred.role], raw)
    }
    sessionStorage.removeItem(LEGACY_KEY)
  } catch {
    /* 무시 — 옛 항목은 그대로 남는다 */
  }
}

function parseLegacy(raw: string): RoomCredential | null {
  try {
    const cred = JSON.parse(raw) as RoomCredential
    if (!cred || (cred.role !== 'host' && cred.role !== 'viewer')) return null
    return cred
  } catch {
    return null
  }
}

export function saveCredential(cred: RoomCredential): void {
  migrateLegacy()
  try {
    sessionStorage.setItem(KEYS[cred.role], JSON.stringify(cred))
  } catch {
    /* 무시 */
  }
}

function loadSlot(role: RoomCredential['role'], room?: string): RoomCredential | null {
  migrateLegacy()
  try {
    // 이행 쓰기가 실패해 옛 항목만 남은 경우의 폴백(TASK_sec_fix_h #2).
    const raw = sessionStorage.getItem(KEYS[role]) ?? sessionStorage.getItem(LEGACY_KEY)
    if (!raw) return null
    const cred = JSON.parse(raw) as RoomCredential
    if (cred.role !== role) return null
    if (room && cred.room !== room) return null
    return cred
  } catch {
    return null
  }
}

/**
 * `role` 을 주면 그 칸만 본다(호스트 화면은 host 칸만). 없으면 viewer →
 * host 순으로 본다 — 뷰어 화면은 자기 방의 host 자격으로도 들어갈 수
 * 있다(host 토큰은 viewer 범위를 포함한다).
 */
export function loadCredential(
  room?: string,
  role?: RoomCredential['role'],
): RoomCredential | null {
  if (role) return loadSlot(role, room)
  return loadSlot('viewer', room) ?? loadSlot('host', room)
}

/** `role` 을 주면 그 칸만, 없으면 둘 다 지운다. */
export function clearCredential(role?: RoomCredential['role']): void {
  try {
    if (role) {
      sessionStorage.removeItem(KEYS[role])
      // 이행 실패로 남은 옛 항목도 역할이 같으면 함께 지운다(폴백 부활 방지).
      const legacy = sessionStorage.getItem(LEGACY_KEY)
      if (legacy !== null && parseLegacy(legacy)?.role === role) {
        sessionStorage.removeItem(LEGACY_KEY)
      }
    } else for (const k of [...Object.values(KEYS), LEGACY_KEY]) sessionStorage.removeItem(k)
  } catch {
    /* 무시 */
  }
}

/**
 * §10.2 직통 링크 — `/v/{room}#k=<view_key>`.
 * fragment 라 서버 로그에 남지 않는다. 읽은 즉시 URL 에서 지운다.
 */
export function takeKeyFromFragment(): string | null {
  if (typeof location === 'undefined') return null
  const hash = location.hash
  // 서버 키는 `secrets.token_urlsafe` — **base64url** 이다([A-Za-z0-9_-]).
  // 16진수만 받던 이전 정규식은 실제 키의 대문자·`_`·`-` 를 만나면 매칭에
  // 실패해 직통 링크가 통째로 죽었다(그리고 "자격 없음" 으로 표시됐다).
  const match = /[#&]k=([A-Za-z0-9_-]{16,64})/.exec(hash)
  if (!match?.[1]) return null
  const key = match[1]
  // 주소창·공유 스크린샷에 키가 남지 않게 즉시 제거
  history.replaceState(null, '', location.pathname + location.search)
  return key
}
